import { animate, stagger, inView } from "motion";

/**
 * Unified scroll-triggered entrance animations for all card layouts.
 * Uses Motion v12 (WAAPI) — replaces the old IntersectionObserver + CSS
 * keyframe systems with a single, consistent approach.
 *
 * Supported selectors:
 *   .is-style-himml-1          (block style)
 *   .is-style-himml-cards      (block style, alias for legacy .himml-cards)
 *   .himml-cards                (legacy manual class)
 *   .himml-frontpage-cards      (legacy manual class)
 *   .himml-card-columns         (legacy manual class)
 *   .is-style-frontpage-cards   (block style)
 *   .himml-frontpage-cards-2    (legacy manual class)
 *   .is-style-frontpage-cards-2 (block style)
 */

const isSingleColumn = (cards) => {
  if (cards.length < 2) return true;
  const firstLeft = cards[0].getBoundingClientRect().left;
  return cards.every(
    (card) => Math.abs(card.getBoundingClientRect().left - firstLeft) < 8
  );
};

/**
 * Animate a set of cards within a section container.
 */
const animateSection = (section, cards) => {
  if (!cards.length) return;

  // Guard against replays: Motion's `inView` fires its callback every time the
  // section re-enters the viewport — and load-time layout shifts near the hero
  // can trigger enter → leave → enter within the same page load. Because the
  // keyframes reset opacity to 0, each fire replayed the entrance (the "animated
  // twice" bug). Run the entrance exactly once, then stop observing.
  let hasAnimated = false;
  let stop;

  stop = inView(
    section,
    () => {
      if (hasAnimated) return;
      hasAnimated = true;
      if (typeof stop === "function") stop();

      const singleCol = isSingleColumn(cards);

      animate(
        cards,
        {
          opacity: [0, 1],
          y: [28, 0],
          filter: ["blur(2px)", "blur(0px)"],
        },
        {
          duration: 0.6,
          delay: stagger(singleCol ? 0 : 0.12),
          easing: [0.22, 1, 0.36, 1],
        }
      ).finished.then(() => {
        cards.forEach((card) => {
          card.style.removeProperty("opacity");
          card.style.removeProperty("transform");
          card.style.removeProperty("filter");
          card.classList.add("motion-entered");
        });
      });
    },
    { amount: 0.12 }
  );
};

/**
 * Selectors grouped by animation target.
 * Each section's direct .wp-block-column children are animated.
 */
const CARD_SECTION_SELECTORS = [
  ".wp-block-columns.is-style-himml-1",
  ".wp-block-columns.is-style-himml-cards",
  ".wp-block-columns.himml-cards",
  ".wp-block-columns.himml-frontpage-cards",
  ".wp-block-columns.himml-card-columns",
  ".wp-block-columns.is-style-frontpage-cards",
  ".wp-block-columns.himml-frontpage-cards-2",
  ".wp-block-columns.is-style-frontpage-cards-2",
].join(",\n");

export const initHimmlCardsMotion = ({ motionEnabled }) => {
  if (!motionEnabled) return;

  const sections = document.querySelectorAll(CARD_SECTION_SELECTORS);

  sections.forEach((section) => {
    const cards = [...section.querySelectorAll(":scope > .wp-block-column")];
    animateSection(section, cards);
  });
};
